import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { TypedMemo } from '../../../../common/types';
import { ErrorAPIResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { Panel } from '../../../components/primitives/Panel';
import { SerializedReel } from '../../types';
import { ReelStage, isReelWorking } from './reelStages';
import { ReelStageList } from './ReelStageList';
import { ReelFailNotice } from './ReelFailNotice';
import { reelVoiceMessages } from './reelMessages';
import { reelProgressMessages } from './reelProgressMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole AI reel flow; each part uses its own slice of it */
import styles from './PromoteReels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelProgressProps {
  reel: SerializedReel;
  lastSeenStage: ReelStage | null;
  /** The release title, for the wait note and the poster. */
  releaseTitle?: string;
  artistName?: string;
  isRetrying: boolean;
  /** The error of a failed "Try again". */
  tryAgainError?: ErrorAPIResult['error'];
  onTryAgain: () => void;
  onChangeSong: () => void;
  onChangeSettings: () => void;
}

/**
 * The wait screen and the failed screen of one reel (design b1 to b4). The API gives no
 * percent, so the poster shows a shimmer and the list shows the stage only. There is no cancel.
 */
const ReelProgressComponent: React.FC<ReelProgressProps> = (props) => {
  const {
    reel,
    lastSeenStage,
    releaseTitle,
    artistName,
    isRetrying,
    tryAgainError,
    onTryAgain,
    onChangeSong,
    onChangeSettings,
  } = props;
  const intl = useIntl();
  const isWorking = isReelWorking(reel.status);
  const isFailed = reel.status === 'failed';
  const voiceName = reel.voiceId === null ? null : intl.formatMessage(reelVoiceMessages[reel.voiceId].label);
  const posterTop = useMemo(
    () => [artistName, releaseTitle].filter((part): part is string => part !== undefined).join(' · '),
    [artistName, releaseTitle]
  );

  return (
    <Panel className={styles.progress}>
      <div className={styles.poster}>
        {isWorking && <div className={styles.posterShimmer} />}
        {posterTop !== '' && !isFailed && <div className={styles.posterTop}>{posterTop}</div>}
        {isFailed && (
          <div className={styles.posterMid}>
            <span className={styles.failedPill}>{intl.formatMessage(reelProgressMessages.failedPill)}</span>
          </div>
        )}
      </div>
      <div className={styles.progressSide}>
        <ReelStageList
          status={reel.status}
          failReason={reel.failReason}
          lastSeenStage={lastSeenStage}
          voiceName={voiceName}
          captionCount={reel.summary === null ? null : reel.summary.captions.length}
        />
        {isWorking && (
          <>
            <div className={styles.hair} />
            <div className={styles.note}>
              <FontAwesomeIcon icon={faClock} className={styles.noteIcon} />
              <span>
                {intl.formatMessage(reelProgressMessages.waitNote, {
                  release: releaseTitle ?? intl.formatMessage(reelProgressMessages.thisRelease),
                })}
              </span>
            </div>
          </>
        )}
        {isFailed && (
          <ReelFailNotice
            failReason={reel.failReason}
            isRetrying={isRetrying}
            tryAgainError={tryAgainError}
            onTryAgain={onTryAgain}
            onChangeSong={onChangeSong}
            onChangeSettings={onChangeSettings}
          />
        )}
      </div>
    </Panel>
  );
};

export const ReelProgress = TypedMemo(ReelProgressComponent);
